<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ProSelect from './ProSelect.vue'
import AppIcon from '@/components/AppIcon.vue'

const props = defineProps<{
  page: number
  pageSize: number
  total: number
  pageSizes?: number[]
}>()

const emit = defineEmits<{
  'update:page': [value: number]
  'update:pageSize': [value: number]
}>()

const jump = ref('')
const sizes = computed(() => props.pageSizes ?? [10, 20, 50, 100])
const maxPage = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)))
const sizeOptions = computed(() => sizes.value.map(value => ({ value: String(value), label: `${value} 条/页` })))
const pageItems = computed<Array<number | '…'>>(() => {
  const total = maxPage.value
  const current = Math.min(props.page, total)
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total]
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total]
  return [1, '…', current - 1, current, current + 1, '…', total]
})

function go(page: number): void {
  const next = Math.min(maxPage.value, Math.max(1, Math.round(page)))
  if (next !== props.page) emit('update:page', next)
}

function jumpTo(): void {
  const value = Number(jump.value)
  if (Number.isFinite(value) && value > 0) go(value)
  jump.value = ''
}

watch(() => props.pageSize, () => {
  if (props.page > maxPage.value) go(maxPage.value)
})
</script>

<template>
  <div class="pagination" role="navigation" aria-label="列表分页">
    <span class="pagination__total">共 {{ total.toLocaleString('zh-CN') }} 条</span>
    <div class="pagination__pages">
      <button type="button" class="page-btn" :disabled="page <= 1" aria-label="上一页" @click="go(page - 1)"><AppIcon name="chevron-left" :size="13" /></button>
      <template v-for="(item, index) in pageItems" :key="`${item}-${index}`">
        <span v-if="item === '…'" class="page-ellipsis">…</span>
        <button
          v-else
          type="button"
          class="page-btn"
          :class="{ 'page-btn--active': item === page }"
          :aria-current="item === page ? 'page' : undefined"
          @click="go(item)"
        >{{ item }}</button>
      </template>
      <button type="button" class="page-btn" :disabled="page >= maxPage" aria-label="下一页" @click="go(page + 1)"><AppIcon name="chevron-right" :size="13" /></button>
    </div>
    <label class="pagination__size">
      <span class="visually-hidden">每页条数</span>
      <ProSelect
        :model-value="String(pageSize)"
        :options="sizeOptions"
        :filterable="false"
        aria-label="每页条数"
        @update:model-value="emit('update:pageSize', Number($event))"
      />
    </label>
    <label class="pagination__jump">
      <span>跳至</span>
      <el-input v-model="jump" size="small" inputmode="numeric" aria-label="跳转页码" @keyup.enter="jumpTo" @blur="jumpTo" />
      <span>页</span>
    </label>
  </div>
</template>

<style scoped>
.page-ellipsis { width: 22px; text-align: center; color: var(--c-faint); font-size: 11px; }
</style>
